import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import RequireAccess, {
  HomeRedirect,
  NotFoundPage,
  RequireEmployee,
} from "../components/layout/AccessStates";
import usePermissions from "../hooks/usePermissions";
import useCurrentUser from "../hooks/useCurrentUser";

// My view (employee screens, moved from the employee portal)
import MeLayout from "../me/components/layout/Layout";
import MyDashboardPage from "../me/pages/Dashboard/DashboardPage";
import MyAttendancePage from "../me/pages/Attendance/AttendancePage";
import MyDtrPage from "../me/pages/Dtr/MyDtrPage";
import MyOvertimePage from "../me/pages/Overtime/MyOvertimePage";
import MyLeavesPage from "../me/pages/Leave/MyLeavesPage";
import MyAssetsPage from "../me/pages/Assets/MyAssetsPage";
import MyEvaluationsPage from "../me/pages/Performance/MyEvaluationsPage";
import MyEvaluationDetailPage from "../me/pages/Performance/MyEvaluationDetailPage";
import EvaluationsToRatePage from "../me/pages/Performance/EvaluationsToRatePage";
import RateEvaluationPage from "../me/pages/Performance/RateEvaluationPage";
import MyLearningPage from "../me/pages/Learning/MyLearningPage";
import SeminarCheckInPage from "../me/pages/Learning/SeminarCheckInPage";
import MyProfilePage from "../me/pages/Profile/MyProfilePage";
import MyNotificationsPage from "../me/pages/Notifications/MyNotificationsPage";
import NotificationsPage from "../pages/notifications/NotificationsPage";
import { LEGACY_PREFIXES, toMyViewPath } from "../me/utils/legacyLinks";

// Auth
import LoginPage from "../pages/auth/LoginPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";

// Dashboard
import DashboardPage from "../pages/dashboard/DashboardPage";

// Employees
import EmployeesPage from "../pages/employees/EmployeesPage";
import EmployeeProfilePage from "../pages/employees/EmployeeProfilePage";
import EmployeeImportPage from "../pages/employees/EmployeeImportPage";

// Leave
import LeaveManagementPage from "../pages/leave/LeaveManagementPage";
import EmployeeLeavePage from "../pages/leave/EmployeeLeavePage";
import LeaveTypesPage from "../pages/leave/LeaveTypesPage";
import LeaveApprovalsPage from "../pages/leave/LeaveApprovalsPage";
import FileLeavePage from "../pages/leave/FileLeavePage";
import LeaveAllocationPage from "../pages/leave/LeaveAllocationPage";
import ReplenishRunsPage from "../pages/leave/ReplenishRunsPage";

// Settings
import SettingsPage from "../pages/settings/SettingsPage";
import BranchPage from "../pages/settings/master-data/BranchPage";
import ClientPage from "../pages/settings/master-data/ClientPage";
import DepartmentPage from "../pages/settings/master-data/DepartmentPage";
import JobTitlePage from "../pages/settings/master-data/JobTitlePage";
import RolePage from "../pages/settings/master-data/RolePage";
import UsersPage from "../pages/settings/master-data/UsersPage";
import ScheduleSyncPage from "../pages/settings/data-sync/ScheduleSyncPage";
import StorageConfigPage from "../pages/settings/data-sync/StorageConfigPage";
import LeaveConfigurationPage from "../pages/settings/leave-config/LeaveConfigurationPage";

// Approvals
import ApprovalsPage from "../pages/approvals/ApprovalsPage";

// Assets
import AssetRegisterPage from "../pages/assets/AssetRegisterPage";
import AssetDetailPage from "../pages/assets/AssetDetailPage";
import AssetAssignmentsPage from "../pages/assets/AssetAssignmentsPage";
import AssetTypesPage from "../pages/assets/AssetTypesPage";

// Performance
import TemplatesPage from "../pages/performance/TemplatesPage";
import TemplateBuilderPage from "../pages/performance/TemplateBuilderPage";
import EvaluationsPage from "../pages/performance/EvaluationsPage";
import BulkCreateEvaluationsPage from "../pages/performance/BulkCreateEvaluationsPage";
import EvaluationDetailPage from "../pages/performance/EvaluationDetailPage";
import EvaluationPrintPage from "../pages/performance/EvaluationPrintPage";

// Attendance
import AttendancePage from "../pages/attendance/AttendancePage";
import TimePage from "../pages/time/TimePage";
import AttendanceSettingsPage from "../pages/attendance/AttendanceSettingsPage";
import BiometricsPage from "../pages/attendance/BiometricsPage";

// DTR, approvers, module access
import ApproversPage from "../pages/approvers/ApproversPage";
import EvaluationReportsPage from "../pages/performance/EvaluationReportsPage";

// Learning
import SeminarsPage from "../pages/learning/SeminarsPage";
import SeminarDetailPage from "../pages/learning/SeminarDetailPage";
import SkillsPage from "../pages/learning/SkillsPage";

// Logs
import AuditLogsPage from "../pages/logs/AuditLogsPage";
import ImportHistoryPage from "../pages/logs/ImportHistoryPage";

// Administration
import AdministrationPage from "../pages/admin/AdministrationPage";
import RolePermissionsPage from "../pages/admin/RolePermissionsPage";
import EmployeeIdFormatPage from "../pages/admin/EmployeeIdFormatPage";
import ExportPage from "../pages/admin/data-tools/ExportPage";
import ImportPage from "../pages/admin/data-tools/ImportPage";
import DataMappingPage from "../pages/admin/data-tools/DataMappingPage";
import ApprovalRulesPage from "../pages/admin/approval-rules/ApprovalRulesPage";
import NotificationSettingsPage from "../pages/admin/notification-settings/NotificationSettingsPage";
import AnnouncementsPage from "../pages/admin/announcements/AnnouncementsPage";
import HolidaysPage from "../pages/admin/holidays/HolidaysPage";
import DtrCutoffSettingsPage from "../pages/admin/dtr-cutoffs/DtrCutoffSettingsPage";
import CompaniesPage from "../pages/admin/companies/CompaniesPage";

/** Old employee-portal address → its /me page, keeping ids and the query string. */
const LegacyRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={toMyViewPath(pathname) + search} replace />;
};

/**
 * /change-password (old links): signed-out users get the forgot-password reset
 * (email + hiring date); signed-in users get their own change-password form
 * (current + new password), which lives in the top-bar profile popup.
 */
const ChangePasswordRedirect = () => {
  const user = useCurrentUser();
  const { hasHrAccess, employeeId, isLoading } = usePermissions();
  if (!user.userId) return <Navigate to="/forgot-password" replace />;
  if (isLoading) return null;
  const home = hasHrAccess || !employeeId ? "/dashboard" : "/me";
  return <Navigate to={home} replace state={{ openChangePassword: true }} />;
};

/** /leave-request: HR leave approvals for HR users, My leaves for everyone else. */
const LeaveRequestRedirect = () => {
  const { hasHrAccess, isLoading } = usePermissions();
  if (isLoading) return null;
  return (
    <Navigate to={hasHrAccess ? "/leave/approvals" : "/me/leaves"} replace />
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* After login: My view or HR view (see HomeRedirect) */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Old address: signed out → forgot-password reset; signed in → profile with change password */}
      <Route path="/change-password" element={<ChangePasswordRedirect />} />

      {/* My view: the employee's own screens */}
      <Route
        path="/me"
        element={
          <RequireEmployee>
            <MeLayout />
          </RequireEmployee>
        }
      >
        <Route index element={<MyDashboardPage />} />
        <Route path="attendance" element={<MyAttendancePage />} />
        <Route path="dtr" element={<MyDtrPage />} />
        {/* DTR history is the cut-off dropdown now */}
        <Route path="dtr/history" element={<Navigate to="/me/dtr" replace />} />
        <Route path="overtime" element={<MyOvertimePage />} />
        <Route path="leaves" element={<MyLeavesPage />} />
        <Route path="assets" element={<MyAssetsPage />} />
        <Route path="evaluations" element={<MyEvaluationsPage />} />
        <Route path="evaluations/:id" element={<MyEvaluationDetailPage />} />
        <Route path="evaluations-to-rate" element={<EvaluationsToRatePage />} />
        <Route
          path="evaluations-to-rate/:id"
          element={<RateEvaluationPage />}
        />
        <Route path="learning" element={<MyLearningPage />} />
        {/* Seminar QR code lands here (signed-out users come back after login) */}
        <Route path="learning/check-in" element={<SeminarCheckInPage />} />
        <Route path="profile" element={<MyProfilePage />} />
        <Route path="notifications" element={<MyNotificationsPage />} />
        <Route path="*" element={<Navigate to="/me" replace />} />
      </Route>
      {/* Old employee-portal addresses (bookmarks, dashboard action items) */}
      {LEGACY_PREFIXES.filter(([from]) => from !== "/leave-request").map(
        ([from]) => (
          <Route key={from} path={`${from}/*`} element={<LegacyRedirect />} />
        ),
      )}
      <Route
        path="/notifications"
        element={
          <RequireAccess>
            <NotificationsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/dashboard"
        element={
          <RequireAccess>
            <DashboardPage />
          </RequireAccess>
        }
      />

      {/* Specific employee routes MUST come before the base /employee route */}
      <Route
        path="/employee/new"
        element={
          <RequireAccess module="employees" action="modify">
            <EmployeeProfilePage />
          </RequireAccess>
        }
      />
      <Route
        path="/employee/import"
        element={
          <RequireAccess module="employees" action="modify" company>
            <EmployeeImportPage />
          </RequireAccess>
        }
      />
      <Route
        path="/employee/approvers"
        element={
          <RequireAccess module="employees" company>
            <ApproversPage />
          </RequireAccess>
        }
      />
      <Route
        path="/employee/import/:batchId"
        element={
          <RequireAccess module="employees" action="modify" company>
            <EmployeeImportPage />
          </RequireAccess>
        }
      />
      <Route
        path="/employee/:id"
        element={
          <RequireAccess module="employees">
            <EmployeeProfilePage />
          </RequireAccess>
        }
      />
      <Route
        path="/employee"
        element={
          <RequireAccess module="employees">
            <EmployeesPage />
          </RequireAccess>
        }
      />

      <Route
        path="/settings"
        element={
          <RequireAccess module="company-setup" company>
            <SettingsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/branch"
        element={
          <RequireAccess module="company-setup" company>
            <BranchPage />
          </RequireAccess>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAccess module="users" company>
            <UsersPage />
          </RequireAccess>
        }
      />
      <Route
        path="/job-title"
        element={
          <RequireAccess module="company-setup" company>
            <JobTitlePage />
          </RequireAccess>
        }
      />
      <Route
        path="/department"
        element={
          <RequireAccess module="company-setup" company>
            <DepartmentPage />
          </RequireAccess>
        }
      />
      <Route
        path="/role"
        element={
          <RequireAccess module="company-setup" company>
            <RolePage />
          </RequireAccess>
        }
      />
      <Route
        path="/client"
        element={
          <RequireAccess module="company-setup" company>
            <ClientPage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave-management"
        element={
          <RequireAccess module="leave">
            <LeaveManagementPage />
          </RequireAccess>
        }
      />
      {/* Old leave request list → replaced by Leave Approvals */}
      <Route path="/leave-request" element={<LeaveRequestRedirect />} />
      <Route
        path="/leave/approvals"
        element={
          <RequireAccess module="leave">
            <LeaveApprovalsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave/file"
        element={
          <RequireAccess module="leave" action="modify">
            <FileLeavePage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave/allocation"
        element={
          <RequireAccess module="leave" company>
            <LeaveAllocationPage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave/replenish"
        element={
          <RequireAccess module="leave" company>
            <ReplenishRunsPage />
          </RequireAccess>
        }
      />
      {/* Balances overview → replaced by the allocation screen (same data, editable) */}
      <Route
        path="/leave/balances"
        element={<Navigate to="/leave/allocation" replace />}
      />
      <Route
        path="/leave/employee/:id"
        element={
          <RequireAccess module="leave">
            <EmployeeLeavePage />
          </RequireAccess>
        }
      />
      <Route
        path="/sync-sched"
        element={
          <RequireAccess module="data-tools" company>
            <ScheduleSyncPage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave-type"
        element={
          <RequireAccess module="leave" company>
            <LeaveTypesPage />
          </RequireAccess>
        }
      />
      <Route
        path="/importLogs"
        element={
          <RequireAccess module="data-tools" company>
            <ImportHistoryPage />
          </RequireAccess>
        }
      />
      <Route
        path="/storageConfig"
        element={
          <RequireAccess module="data-tools" company>
            <StorageConfigPage />
          </RequireAccess>
        }
      />
      <Route
        path="/auditLogs"
        element={
          <RequireAccess module="audit-logs">
            <AuditLogsPage />
          </RequireAccess>
        }
      />
      {/* Master data became the Administration hub */}
      <Route path="/masterData" element={<Navigate to="/admin" replace />} />
      <Route
        path="/admin"
        element={
          <RequireAccess>
            <AdministrationPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/roles-permissions"
        element={
          <RequireAccess module="company-setup" company>
            <RolePermissionsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/employee-id-format"
        element={
          <RequireAccess module="company-setup" company>
            <EmployeeIdFormatPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/export"
        element={
          <RequireAccess module="data-tools" company>
            <ExportPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/import"
        element={
          <RequireAccess module="data-tools" company>
            <ImportPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/data-mapping"
        element={
          <RequireAccess module="data-tools" company>
            <DataMappingPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <RequireAccess module="company-setup" company>
            <CompaniesPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <RequireAccess module="announcements">
            <AnnouncementsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/holidays"
        element={
          <RequireAccess module="holidays">
            <HolidaysPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/dtr-cutoffs"
        element={
          <RequireAccess module="company-setup" company>
            <DtrCutoffSettingsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/notification-settings"
        element={
          <RequireAccess module="company-setup" company>
            <NotificationSettingsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/admin/approval-rules"
        element={
          <RequireAccess module="company-setup" company>
            <ApprovalRulesPage />
          </RequireAccess>
        }
      />
      <Route
        path="/leave-config"
        element={
          <RequireAccess module="leave" company>
            <LeaveConfigurationPage />
          </RequireAccess>
        }
      />

      <Route
        path="/assets"
        element={
          <RequireAccess module="assets">
            <AssetRegisterPage />
          </RequireAccess>
        }
      />
      <Route
        path="/assets/assignments"
        element={
          <RequireAccess module="assets">
            <AssetAssignmentsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/assets/:id"
        element={
          <RequireAccess module="assets">
            <AssetDetailPage />
          </RequireAccess>
        }
      />
      <Route
        path="/settings/asset-types"
        element={
          <RequireAccess module="assets" company>
            <AssetTypesPage />
          </RequireAccess>
        }
      />

      <Route
        path="/performance/templates"
        element={
          <RequireAccess module="performance" company>
            <TemplatesPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/templates/new"
        element={
          <RequireAccess module="performance" company>
            <TemplateBuilderPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/templates/:id"
        element={
          <RequireAccess module="performance" company>
            <TemplateBuilderPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/templates/:id/edit"
        element={
          <RequireAccess module="performance" company>
            <TemplateBuilderPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/evaluations"
        element={
          <RequireAccess module="performance">
            <EvaluationsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/evaluations/bulk"
        element={
          <RequireAccess module="performance" company>
            <BulkCreateEvaluationsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/evaluations/:id"
        element={
          <RequireAccess module="performance">
            <EvaluationDetailPage />
          </RequireAccess>
        }
      />
      <Route
        path="/performance/evaluations/:id/print"
        element={
          <RequireAccess module="performance">
            <EvaluationPrintPage />
          </RequireAccess>
        }
      />
      {/* Evaluation sign-off was removed: results go straight to the employee */}
      <Route
        path="/performance/sign-off"
        element={<Navigate to="/performance/evaluations" replace />}
      />
      <Route
        path="/performance/reports"
        element={
          <RequireAccess module="performance" company>
            <EvaluationReportsPage />
          </RequireAccess>
        }
      />

      <Route
        path="/attendance"
        element={
          <RequireAccess module="attendance">
            <AttendancePage />
          </RequireAccess>
        }
      />
      {/* HR Time module: attendance, DTR (cut-off approval), overtime, export */}
      <Route
        path="/time"
        element={
          <RequireAccess any={["attendance", "dtr", "overtime"]}>
            <TimePage />
          </RequireAccess>
        }
      />
      {/* The old weekly DTR screens now live in Time */}
      <Route
        path="/dtr/entries"
        element={<Navigate to="/time?tab=dtr" replace />}
      />
      <Route
        path="/dtr/weekly"
        element={<Navigate to="/time?tab=attendance" replace />}
      />
      <Route
        path="/dtr/cutoffs"
        element={<Navigate to="/time?tab=dtr" replace />}
      />
      <Route
        path="/dtr/export"
        element={<Navigate to="/time?tab=export" replace />}
      />
      {/* Module access was replaced by role permissions */}
      <Route
        path="/settings/module-access"
        element={<Navigate to="/admin/roles-permissions" replace />}
      />
      <Route
        path="/attendance/settings"
        element={
          <RequireAccess module="attendance" company>
            <AttendanceSettingsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/attendance/biometrics"
        element={
          <RequireAccess module="attendance" company>
            <BiometricsPage />
          </RequireAccess>
        }
      />
      {/* Correction requests were removed → check-in validation */}
      <Route
        path="/approvals/corrections/*"
        element={<Navigate to="/approvals?tab=validation" replace />}
      />

      <Route
        path="/learning/seminars"
        element={
          <RequireAccess module="learning">
            <SeminarsPage />
          </RequireAccess>
        }
      />
      <Route
        path="/learning/seminars/:id"
        element={
          <RequireAccess module="learning">
            <SeminarDetailPage />
          </RequireAccess>
        }
      />
      <Route
        path="/learning/skills"
        element={
          <RequireAccess module="learning">
            <SkillsPage />
          </RequireAccess>
        }
      />

      <Route
        path="/approvals"
        element={
          <RequireAccess any={["dtr", "attendance", "overtime"]}>
            <ApprovalsPage />
          </RequireAccess>
        }
      />
      {/* DTR review moved: HR approves each employee's cut-off in Time → DTR */}
      <Route
        path="/approvals/dtr/*"
        element={<Navigate to="/time?tab=dtr" replace />}
      />
      {/* Module roots without their own page */}
      <Route
        path="/leave"
        element={
          <RequireAccess module="leave">
            <Navigate to="/leave/approvals" replace />
          </RequireAccess>
        }
      />
      <Route
        path="*"
        element={
          <RequireAccess>
            <NotFoundPage />
          </RequireAccess>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
